import { motion } from "framer-motion";
import aacharyData from "~/data/about-us/aachary.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { cn } from "~/lib/utils";

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative w-full mb-5"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-6xl text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 className={cn("text-3xl md:text-5xl font-bold text-textColor mb-4", medium)}>Aacharya</h1>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-6" />
            <p className={cn("text-lg md:text-xl text-textSecondaryColor italic max-w-2xl mx-auto", medium)}>
              The Spiritual Leader • Divine Guide • Source of Inspiration
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section
const IntroductionSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -40 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div whileHover={{ scale: 1.01, boxShadow: "0 20px 40px rgba(217, 126, 87, 0.2)" }} className="relative">
          {/* Decorative icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isVisible ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="absolute -top-6 left-6 md:left-12 w-12 h-12 bg-primaryColor rounded-full flex items-center justify-center shadow-lg z-10"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </motion.div>

          <div className="bg-gradient-to-br from-secondaryColor/40 via-white to-secondaryColor/20 p-8 md:p-12 rounded-2xl border-l-4 border-primaryColor shadow-xl">
            <h2 className={cn("text-xl md:text-2xl font-bold text-primaryColor mb-6", medium)}>What is an Aacharya?</h2>
            <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>
              {aacharyData.content.introduction}
            </p>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Three Resolutions Section
const ResolutionsSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const resolutions = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
      title: "Build Temples",
      description: "Install idols of deities",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      title: "Establish Aacharyas",
      description: "Divine spiritual leaders",
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      title: "Compose Scriptures",
      description: "Manifest sect greatness",
    },
  ];

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 md:mb-20 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-4 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            Lord Swaminarayan&apos;s Three Resolutions
          </h2>
          <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed max-w-4xl mx-auto mt-6", normalInter)}>
            {aacharyData.content.swaminarayanResolutions}
          </p>
        </motion.div>

        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {resolutions.map((resolution, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{
                y: -10,
                boxShadow: "0 25px 50px rgba(217, 126, 87, 0.3)",
              }}
              className="bg-gradient-to-br from-white via-secondaryColor/20 to-primaryColor/10 p-8 rounded-xl border-2 border-primaryColor/40 shadow-lg hover:border-primaryColor transition-all duration-300 text-center group"
            >
              <motion.div
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className="w-16 h-16 bg-gradient-to-br from-primaryColor to-primaryColor/70 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md text-white"
              >
                {resolution.icon}
              </motion.div>

              <h3 className={cn("text-xl font-bold text-primaryColor mb-2 group-hover:text-textColor transition-colors", medium)}>
                {resolution.title}
              </h3>

              <p className={cn("text-textSecondaryColor text-sm", normalInter)}>{resolution.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

// Image Section
const ImageSection = () => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-primaryColor/30 hover:border-primaryColor/60 transition-all duration-500"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
          <motion.img
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.5 }}
            src={aacharyData.content.mainImage.src}
            alt={aacharyData.content.mainImage.alt}
            className="w-full aspect-video object-cover"
            loading="lazy"
          />
        </motion.div>
      </div>
    </motion.section>
  );
};

// Sect Division Section
const SectDivisionSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  const divisions = [
    {
      title: "Northern Division",
      deity: "Nar-Narayan Dev",
      temple: "Ahmedabad Temple",
      aacharya: "Ayodhyaprasadji Maharaj",
      father: "Rampratapbhai",
      color: "from-primaryColor/20",
    },
    {
      title: "Southern Division",
      deity: "Lakshmi-Narayan Dev",
      temple: "Vadtal Temple",
      aacharya: "Raghuvirji Maharaj",
      father: "Ichharambhai",
      color: "from-primaryColor/15",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="mb-12 md:mb-20 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-6 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            The Two Divine Thrones
          </h2>
          <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed text-justify", normalInter)}>
            {aacharyData.content.sectDivision}
          </p>
        </motion.div>

        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {divisions.map((division, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{
                y: -8,
                boxShadow: "0 20px 40px rgba(217, 126, 87, 0.25)",
              }}
              className={`bg-gradient-to-br ${division.color} via-secondaryColor/30 to-white p-8 rounded-2xl border-2 border-primaryColor/40 shadow-xl hover:border-primaryColor transition-all duration-300`}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primaryColor rounded-full flex items-center justify-center mr-4 shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                </div>
                <h3 className={cn("text-xl md:text-2xl font-bold text-primaryColor", medium)}>{division.title}</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start">
                  <span className={cn("text-textColor font-semibold min-w-[100px]", medium)}>Deity:</span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{division.deity}</span>
                </div>
                <div className="flex items-start">
                  <span className={cn("text-textColor font-semibold min-w-[100px]", medium)}>Temple:</span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{division.temple}</span>
                </div>
                <div className="flex items-start">
                  <span className={cn("text-textColor font-semibold min-w-[100px]", medium)}>Aacharya:</span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{division.aacharya}</span>
                </div>
                <div className="flex items-start">
                  <span className={cn("text-textColor font-semibold min-w-[100px]", medium)}>Father:</span>
                  <span className={cn("text-textSecondaryColor", normalInter)}>{division.father}</span>
                </div>
              </div>

              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-6 h-1 bg-gradient-to-r from-primaryColor to-transparent"
                style={{ transformOrigin: "left" }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

// Date Section
const DateSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.7 }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-primaryColor/10 via-secondaryColor/30 to-primaryColor/10 p-8 md:p-12 rounded-2xl border-2 border-primaryColor/50 shadow-2xl text-center"
        >
          <motion.div
            initial={{ rotate: 0 }}
            animate={isVisible ? { rotate: [0, 10, -10, 0] } : { rotate: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="w-20 h-20 bg-primaryColor rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </motion.div>

          <h3 className={cn("text-xl md:text-2xl font-bold text-primaryColor mb-4", medium)}>Historic Day of Installation</h3>

          <div className="space-y-2 mb-6">
            <p className={cn("text-2xl md:text-3xl font-bold text-textColor", medium)}>November 21, 1825</p>
            <p className={cn("text-lg text-textSecondaryColor", normalInter)}>Kartak Sud Ekadashi, Vikram Samvat 1882</p>
            <p className={cn("text-base text-textSecondaryColor italic", normalInter)}>Tuesday</p>
          </div>

          <p className={cn("text-textSecondaryColor text-sm md:text-base", normalInter)}>In the presence of thousands of saints and devotees</p>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Sanskrit Verse Section
const SanskritVerseSection = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8 }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-8"
        >
          <h2 className={cn("text-2xl md:text-3xl font-bold text-textColor mb-4 inline-block border-b-4 border-primaryColor pb-2", medium)}>
            From Shikshapatri
          </h2>
          <p className={cn("text-textSecondaryColor text-sm md:text-base italic", normalInter)}>Handwritten by Lord Swaminarayan • Verse 3</p>
        </motion.div>

        <motion.div whileHover={{ scale: 1.01, boxShadow: "0 20px 40px rgba(217, 126, 87, 0.2)" }} className="relative">
          {/* Sanskrit verse card */}
          <div className="bg-gradient-to-br from-secondaryColor/40 via-white to-secondaryColor/20 p-8 md:p-12 rounded-2xl border-4 border-primaryColor/30 shadow-xl mb-6">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                transition={{ delay: 0.4 }}
              >
                <p
                  className={cn("text-primaryColor text-xl md:text-2xl leading-relaxed font-semibold mb-6", normalInter)}
                  dangerouslySetInnerHTML={{ __html: aacharyData.content.sanskritVerse }}
                />
              </motion.div>

              <motion.div
                initial={{ scaleX: 0 }}
                animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-6"
              />

              <motion.div initial={{ opacity: 0 }} animate={isVisible ? { opacity: 1 } : { opacity: 0 }} transition={{ delay: 0.8 }}>
                <p className={cn("text-textSecondaryColor text-base md:text-lg leading-relaxed italic", normalInter)}>
                  {aacharyData.content.translation}
                </p>
              </motion.div>
            </div>
          </div>

          {/* Decorative corner elements */}
          <div className="absolute -top-4 -left-4 w-8 h-8 border-t-4 border-l-4 border-primaryColor rounded-tl-lg" />
          <div className="absolute -top-4 -right-4 w-8 h-8 border-t-4 border-r-4 border-primaryColor rounded-tr-lg" />
          <div className="absolute -bottom-4 -left-4 w-8 h-8 border-b-4 border-l-4 border-primaryColor rounded-bl-lg" />
          <div className="absolute -bottom-4 -right-4 w-8 h-8 border-b-4 border-r-4 border-primaryColor rounded-br-lg" />
        </motion.div>
      </div>
    </motion.section>
  );
};

function Aachary() {
  return (
    <div className="w-full bg-gradient-to-b from-white via-secondaryColor/5 to-white">
      {/* py-8 md:py-16 */}
      <div className="w-full">
        <HeroSection />
        <IntroductionSection />
        <ResolutionsSection />
        <ImageSection />
        <SectDivisionSection />
        <DateSection />
        <SanskritVerseSection />
      </div>
    </div>
  );
}

export default Aachary;
